import { config } from '../../../config.js';

export const ValidatorMixin = (Base) => class extends Base {
    
    // Mapa de estrategias de validación (Regex y Lógica de Negocio)
    #strategies = new Map([
        ['email', {
            validate: (val) => {
                const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!regex.test(val)) return false;
                
                const domain = val.split('@')[1]?.toLowerCase();
                const ALLOWED_DOMAINS = new Set([
                    'gmail.com',
                    'outlook.com',
                    'hotmail.com',
                    'icloud.com',
                    'mac.com',
                    'me.com',
                    'qrsmart.click',
                    'qrsmart.menu',
                    'jjco.dev'
                ]);
                if (process.env.NODE_ENV === 'development') {
                    ALLOWED_DOMAINS.add('mail.dev');
                    ALLOWED_DOMAINS.add('mail.net');
                    ALLOWED_DOMAINS.add('mail.test');
                }
                return ALLOWED_DOMAINS.has(domain);
            },
            msg: 'Solo se permiten correos de Gmail, Apple, Microsoft.)'
        }],
        ['password', {
            validate: (val) => {
                // Developer Experience: Bypass en desarrollo para no sufrir
                if (config.isDev) return true;

                if (val.length < 8) return false;
                const hasLetter = /[a-zA-Z]/.test(val);
                const hasNum = /[0-9]/.test(val);
                return hasLetter && hasNum;
            },
            msg: 'Mínimo 8 caracteres, debe incluir letras y números'
        }],
        ['text', {
            // Solo validación pasiva, la restricción activa se hace en setupConstraints
            validate: (val) => val.length <= 125,
            msg: 'Máximo 125 caracteres'
        }],
        ['tel', {
            validate: (val) => /^[0-9]*$/.test(val) && val.length <= 15,
            msg: 'Solo números, máximo 15 dígitos'
        }],
        ['number', {
            validate: (val) => /^[0-9]*$/.test(val),
            msg: 'Solo se permiten números'
        }]
    ]);

    onMount() {
        super.onMount();
        const input = this.root.querySelector('input');
        const type = this.getAttribute('type') || 'text';

        // 1. Configurar restricciones físicas (Bloqueo de teclas / MaxLength)
        this.#setupConstraints(input, type);

        // 2. Configurar validación en tiempo real
        input.addEventListener('input', () => this.#runValidation(input, type));
        
        // Ejecutar validación inicial si hay valor
        if(input.value) this.#runValidation(input, type);
    }

    #setupConstraints(input, type) {
        // Regla: Texto max 125 chars
        if (type === 'text') {
            input.setAttribute('maxlength', '125');
        }
        
        // Regla: Tel max 15 chars + Solo números
        if (type === 'tel') {
            input.setAttribute('maxlength', '15');
            this.#blockNonNumeric(input);
        }

        // Regla: Number solo números (Bloqueo estricto, evita 'e', '.', etc si no se desean)
        if (type === 'number') {
            this.#blockNonNumeric(input);
        }
    }

    #blockNonNumeric(input) {
        input.addEventListener('keydown', (e) => {
            // Permitir teclas de control: Backspace, Tab, Enter, Arrows, Delete, Copy/Paste (Ctrl+C/V)
            const allowedKeys = ['Backspace', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Delete'];
            if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;

            // Bloquear si no es número
            if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
            }
        });
    }

    #runValidation(input, type) {
        const strategy = this.#strategies.get(type);
        if (!strategy) return; // Si no hay estrategia, es válido por defecto

        const isValid = strategy.validate(input.value);

        if (!isValid) {
            // Seteamos el error nativo en el input interno
            input.setCustomValidity(strategy.msg);
        } else {
            // Limpiamos el error
            input.setCustomValidity('');
        }
        
        // IMPORTANTE: Disparamos un evento para que la clase base (FlowInput)
        // se entere y actualice ElementInternals si es necesario.
        input.reportValidity();
    }
};
